import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { getLeverage } from '@ui/utils/trade/getLeverage';
import { getLeverageMarginByNotional } from '@ui/utils/trade/getLeverageMarginByNotional';
import { isSafeAfterMarginRemoval } from '@ui/utils/trade/isSafeAfterMarginRemoval';
import { useMemo } from 'react';

import { useMinLeverage } from '../../AddCollateral/hooks/useMinLeverage';
import { validateWithdrawCollateralAmount } from '../../TradeModule/utils/validateWithdrawCollateralAmount';
import { useMaxRemovableCollateral } from './useMaxRemovableCollateral';
import { useWithdrawCollateralInput } from './useWithdrawCollateralInput';

export const useWithdrawCollateralValidation = ({
  position,
}: {
  position: Position;
}) => {
  const input = useWithdrawCollateralInput((s) => s.input);
  const payTokenPrice = usePayTokenPriceLatest(position.collateralToken);
  const pairMarketPrice = usePriceLatest(position.pair.baseSymbol);
  const maxRemovableResult = useMaxRemovableCollateral({ position });
  const minLeverage = useMinLeverage({ position });

  const pairInfos = useTradePairInfos();
  const pairInfo = useMemo(() => {
    return pairInfos?.find(
      (v) => v.address.toLowerCase() === position.pair.address.toLowerCase(),
    );
  }, [pairInfos, position.pair.address]);

  // Match the contract: notional = qty × market price, tier = first whose
  // notionalUsd cap covers it. Falls back to entry notional + first tier when
  // market price isn't ready, just to keep validation usable (the on-chain
  // check still gates the actual call).
  const notionalUsd = useMemo(
    () =>
      pairMarketPrice
        ? position.size.times(pairMarketPrice)
        : position.size.times(position.entryPrice),
    [pairMarketPrice, position.size, position.entryPrice],
  );

  const maxLeverage = useMemo(() => {
    if (!pairInfo) return 0;
    
return (
      getLeverageMarginByNotional({
        leverageMargins: pairInfo.leverageMargins,
        notionalUsd,
      })?.maxLeverage ?? 0
    );
  }, [pairInfo, notionalUsd]);

  return useMemo(() => {
    if (!payTokenPrice || !maxRemovableResult) {
      return { error: undefined as undefined };
    }

    const { maxRemovable, limitReason } = maxRemovableResult;

    if (maxRemovable.lte(0) && input.value.token && input.value.token.gt(0)) {
      if (limitReason === 'takeProfit') {
        return { error: 'exceeds_max_removable_take_profit' as const };
      }
      if (limitReason === 'liquidation') {
        return { error: 'exceeds_max_removable_liquidation' as const };
      }

      return { error: 'no_removable_margin' as const };
    }

    const collateralUsd = position.getCollateralUsd(payTokenPrice);
    const nextCollateralUsd = collateralUsd.minus(input.value.usd ?? 0);
    const nextLeverage = getLeverage({
      collateralUsd: nextCollateralUsd,
      sizeUsd: notionalUsd,
    });

    const baseValidation = validateWithdrawCollateralAmount({
      input: input.value,
      maxRemovable,
      maxRemovableLimitReason: limitReason,
      collateral: position.collateral,
      minLeverage,
      nextLeverage,
      maxLeverage,
      isDegen: position.pair.isDegenPair,
    });

    if (
      baseValidation.error ||
      !input.value.token ||
      !pairInfo ||
      !pairMarketPrice
    ) {
      return baseValidation;
    }

    const collateralAfter = position.collateral.minus(input.value.token);
    if (
      !isSafeAfterMarginRemoval({
        position,
        pairInfo,
        pairMarketPrice,
        collateralMarketPrice: payTokenPrice,
        collateralAfterRemoval: collateralAfter,
      })
    ) {
      return { error: 'liquidatable_after_removal' as const };
    }

    return baseValidation;
  }, [
    input.value,
    maxRemovableResult,
    maxLeverage,
    minLeverage,
    notionalUsd,
    pairInfo,
    pairMarketPrice,
    payTokenPrice,
    position,
  ]);
};
